<!-- OUTBOX — and it is AUTHORITATIVE.
     Every rendered fax lands here whether or not it was shared, with an
     explicit HELD or SENT on each. That distinction is the fix for the single
     most damaging finding in testing: the app performs a delivery it does not
     make, and without a state on the row there is no way to tell a fax you
     sent from one you rendered and then closed the share sheet on.

     Deleting uses removeWithUndo — the house style bans confirm dialogs for
     anything reversible. -->
<template>
  <IonPage>
    <AppHeader title="Outbox" back="/tabs/machine" />
    <IonContent :fullscreen="true">

      <div v-if="!faxs.length" class="pad center" style="margin-top: var(--sp-7)">
        <h2 style="margin: 0 0 var(--sp-2)">Nothing sent yet</h2>
        <p class="silkscreen says">
          {{ say('OUTBOX EMPTY. NOTHING HAS LEFT THIS BUILDING.', 'The out tray is empty.') }}
        </p>
        <p class="muted" style="margin-top: var(--sp-2)">
          Every fax you make lands here, whether or not you send it on.
        </p>
        <IonButton expand="block" style="margin-top: var(--sp-4)" @click="$router.push('/tabs/machine/compose')">
          LOAD ORIGINAL
        </IonButton>
      </div>

      <template v-else>
        <!-- A SWIPE NOBODY DISCOVERS IS A FEATURE NOBODY HAS. Swipe-to-delete
             is the platform's own gesture and it is right here, but it is also
             invisible, so the machine says so — for three sessions, and then
             it stops. `showUntilLearned` is the chassis's counter; the
             hundredth visit to this screen is not the first one, and the day
             a hint is written every visit is. -->
        <p v-if="showSwipeHint" class="silkscreen pad" style="margin: var(--sp-4) 0 0">
          SWIPE A LINE LEFT TO SHRED IT.
        </p>

        <IonList inset class="list-inset">
          <IonItemSliding v-for="f in newestFirst" :key="f.id">
            <IonItem button :detail="true" @click="open(f)">
              <!-- Only render a thumbnail that is actually an image. Demo
                   fixtures store a plain string, and a broken <img> is worse
                   than none. -->
              <div v-if="isImage(f.thumb)" slot="start" class="sheet" style="width:56px">
                <img class="thumb" :src="f.thumb" alt="">
              </div>
              <IonLabel>
                <h3>{{ f.toName || 'NO RECIPIENT' }}</h3>
                <p>{{ f.subject || (f.kind === 'memo' ? 'Cover sheet' : 'One photo') }}</p>
                <p class="silkscreen says">{{ stamp(f.createdAt) }} · {{ stateOf(f) }}</p>
              </IonLabel>
            </IonItem>
            <IonItemOptions side="end">
              <IonItemOption color="danger" @click="destroy(f)">Delete</IonItemOption>
            </IonItemOptions>
          </IonItemSliding>
        </IonList>

        <!-- THE WAY ON, not just the way back. The empty outbox offered LOAD
             ORIGINAL and the full one offered nothing, so the moment the app
             started working the only route to a second fax was back out to the
             machine — which is the screen this one is stacked on top of. A
             stack shell has no chrome to hang this in; SHELL.md's own answer is
             a row at the end of the list, and this is it. -->
        <div class="pad" style="margin-top: var(--sp-4)">
          <IonButton expand="block" fill="outline" @click="newFax">
            LOAD ANOTHER ORIGINAL
          </IonButton>
        </div>
      </template>

      <div class="tail" />
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import {
  IonPage, IonContent, IonList, IonItem, IonLabel, IonButton,
  IonItemSliding, IonItemOptions, IonItemOption,
} from '@ionic/vue';
import { useRouter } from 'vue-router';
import { AppHeader, showUntilLearned } from '../core/ui';
import { haptic } from '../core/util.js';
import { remove as removeWithUndo } from '../core/undo.js';
import { faxs, removeFax, storeReady } from '../store.js';
import { stamp } from '../fax/machine.js';
import { say } from '../fax/voice.js';

const router = useRouter();
// Read ONCE, at setup, not in the template: `showUntilLearned` increments the
// counter as a side effect, so a computed re-evaluating on every render would
// burn all three sessions inside one visit.
const showSwipeHint = showUntilLearned('outbox-swipe-delete', 3);

/**
 * NEWEST FIRST — which it was not, and only after a reload.
 *
 * `addFax` PREPENDS, so within a single session the array is already in the
 * right order and the list looked correct. But the store hydrates from
 * IndexedDB with `records.all()`, which returns rows in key order, so the
 * second time the app was opened the outbox was oldest-first with today's fax
 * at the bottom of the scroll — and nothing on the row said so, because there
 * was no date on it either.
 *
 * A copy, because `Array.prototype.sort` mutates and this is the live store.
 */
const newestFirst = computed(() => [...faxs.value].sort(
  (a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
      || Number(b.id) - Number(a.id),
));

// `from=outbox` is what lets Back on the preview come back HERE instead of
// dropping the person on the home screen with their place in the list lost.
const open = (f) => {
  haptic('tick');
  return router.push({ path: `/tabs/machine/${f.id}/sending`, query: { from: 'outbox' } });
};
const newFax = () => { haptic('tick'); router.push('/tabs/machine/compose'); };
const isImage = (v) => typeof v === 'string' && v.startsWith('data:image');

/**
 * THREE STATES, NOT TWO.
 *
 * `draft` is a row that was queued on the cover sheet and then abandoned at the
 * preview — the machine never printed it. It was being labelled
 * "HELD · NOT YET COLLECTED", which claims a page exists in the out tray, and
 * the row had no thumbnail because there was no page. Saying so is the whole
 * job of this column.
 */
function stateOf(f) {
  if (f.status === 'sent') return say('SENT', 'Sent');
  if (f.status === 'held') return say('HELD · NOT YET COLLECTED', 'Held — you have not sent this on');
  return say('NOT PRINTED · TAP TO FINISH', 'Not printed yet — tap to finish it');
}

function destroy(f) {
  // `pop` is the kind for something DESTROYED. The row sliding out is the
  // visible half; this is the half you feel, and it is what stops a
  // half-registered swipe reading as "did that work".
  haptic('pop');
  // The row leaves the list immediately and is only really deleted when the
  // undo window closes — the house style bans a confirm dialog for anything
  // reversible, and this is reversible.
  const keep = { ...f };
  faxs.value = faxs.value.filter((r) => r.id !== f.id);
  removeWithUndo({
    text: 'FAX DELETED.',
    commit: () => removeFax(keep.id),
    revert: () => { faxs.value = [keep, ...faxs.value]; },
  });
}

onMounted(() => storeReady);
</script>
